import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ClipboardList,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  CheckSquare,
  ArrowRight,
  Search,
  Filter,
  Eye,
  Building,
  MapPin,
  DollarSign,
  Laptop,
  FileText,
  RotateCw,
  LifeBuoy,
  ExternalLink,
  SlidersHorizontal,
  Wrench,
} from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { DataTable, type Column } from '../../../components/DataTable';
import { Pagination } from '../../../components/Pagination';
import { UserAvatar } from '../../../components/UserAvatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/Dialog';
import { useAssetStore, type AssetRequest, type Asset } from '../../../store/assetStore';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';
import { useTicketStore } from '../../../store/ticketStore';
import { useAuthStore } from '../../../store/authStore';
import { PRIORITY_TONE, RequestStatus, useAssetGrant } from './requestUi';
import { PRESET_CATEGORIES } from './assetRequestSpecs';

const fieldClass =
  'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary';

interface NewRequestForm {
  asset_type: string;
  category: string;
  quantity: string;
  priority: AssetRequest['priority'];
  urgency: string;
  department: string;
  delivery_location: string;
  estimated_cost: string;
  ticket_id: string;
  reason: string;
  specifications: Record<string, string>;
}

const EMPTY_FORM: NewRequestForm = {
  asset_type: '',
  category: '',
  quantity: '1',
  priority: 'medium',
  urgency: 'standard',
  department: '',
  delivery_location: '',
  estimated_cost: '',
  ticket_id: '',
  reason: '',
  specifications: {},
};

interface IssueForm {
  assetId: string;
  problemType: string;
  priority: AssetRequest['priority'];
  description: string;
}

const EMPTY_ISSUE: IssueForm = {
  assetId: '',
  problemType: 'Not working',
  priority: 'medium',
  description: '',
};

const ISSUE_PROBLEM_TYPES = [
  'Not working',
  'Physically damaged',
  'Running slow / performance',
  'Needs repair / service',
  'Accessory / part missing',
  'Other',
];

type TabFilter = 'all' | 'pending' | 'approved' | 'fulfilled' | 'rejected';

/**
 * Unified Requests & Approvals View
 * Merges employee hardware requisition submission and managerial approval lifecycle into
 * a single context with real-time status tracking, filtering, and audit inspection.
 */
export function AssetRequestsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requests, isLoading, fetchRequests, createRequest, updateRequestStatus, assets, fetchAssets, fetchMyAssets } = useAssetStore();
  const { categories, fetchCategories } = useAssetCatalogStore();
  const { createTicket } = useTicketStore();
  const { user } = useAuthStore();
  const isManager = useAssetGrant('Asset Requests', 'Edit');

  // URL-driven tab filter
  const activeTab = (searchParams.get('tab') as TabFilter) || 'all';
  const setActiveTab = (tab: TabFilter) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'all') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true }
    );
  };

  // Search & Filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modals & Action States
  const [openNewModal, setOpenNewModal] = useState(false);
  const [modalMode, setModalMode] = useState<'request' | 'issue'>('request');
  const [form, setForm] = useState<NewRequestForm>(EMPTY_FORM);
  const [issueForm, setIssueForm] = useState<IssueForm>(EMPTY_ISSUE);
  const [myAssets, setMyAssets] = useState<Asset[]>([]);
  const [detailRequest, setDetailRequest] = useState<AssetRequest | null>(null);

  // Synchronize active detail modal with store updates (auto-reflects generated support tickets)
  const activeDetailRequest = useMemo(() => {
    if (!detailRequest) return null;
    return requests.find((r) => r.id === detailRequest.id) || detailRequest;
  }, [requests, detailRequest]);

  const [reviewDialog, setReviewDialog] = useState<{
    request: AssetRequest;
    status: 'approved' | 'rejected';
    notes: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
    fetchCategories();
    fetchMyAssets().then(setMyAssets).catch(() => setMyAssets([]));
    // Managers/admins can also report on behalf of others — load the full list for them.
    if (isManager) fetchAssets();
  }, [fetchRequests, fetchCategories, fetchMyAssets, fetchAssets, isManager]);

  // Summary counts
  const counts = useMemo(() => {
    return {
      all: requests.length,
      pending: requests.filter((r) => r.status === 'pending').length,
      approved: requests.filter((r) => r.status === 'approved').length,
      fulfilled: requests.filter((r) => r.status === 'fulfilled').length,
      rejected: requests.filter((r) => r.status === 'rejected').length,
    };
  }, [requests]);

  // Filtered & Searched data
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      // Tab filter
      if (activeTab !== 'all' && r.status !== activeTab) {
        return false;
      }
      // Priority filter
      if (priorityFilter !== 'all' && r.priority !== priorityFilter) {
        return false;
      }
      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const num = (r.request_number || r.requestNumber || '').toLowerCase();
        const requester = (r.requester_name || '').toLowerCase();
        const email = (r.requester_email || '').toLowerCase();
        const type = (r.asset_type || '').toLowerCase();
        const dept = (r.department || r.requester_department || '').toLowerCase();
        const reason = (r.reason || r.notes || '').toLowerCase();
        const notes = (r.approval_notes || '').toLowerCase();
        if (
          !num.includes(q) &&
          !requester.includes(q) &&
          !email.includes(q) &&
          !type.includes(q) &&
          !dept.includes(q) &&
          !reason.includes(q) &&
          !notes.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [requests, activeTab, priorityFilter, searchQuery]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRequests.slice(start, start + pageSize);
  }, [filteredRequests, page, pageSize]);

  // Reset page on filter changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, priorityFilter, searchQuery, pageSize]);

  // Handle new request submission
  const handleSubmitNew = async () => {
    const assetType = form.asset_type.trim();
    if (!assetType) {
      toast.error('Please tell us what you need');
      return;
    }
    if (assetType.length > 100) {
      toast.error('Asset name is too long (max 100 characters)');
      return;
    }
    // Quantity: whole number, at least 1.
    const quantity = Math.max(1, Math.floor(Number(form.quantity) || 1));

    setBusy('new');
    try {
      const specifications = { ...form.specifications, quantity: String(quantity) };
      const createdReq = await createRequest({
        asset_type: assetType,
        quantity,
        priority: form.priority,
        urgency: form.priority, // keep urgency aligned with the chosen priority
        department: form.department.trim() || undefined,
        delivery_location: form.delivery_location.trim() || undefined,
        reason: form.reason.trim() || undefined,
        specifications,
      });

      const ticketNum = createdReq?.ticket_id || createdReq?.ticket_no;
      if (ticketNum) {
        toast.success(`Requisition ${createdReq.request_number || 'created'}! Support Ticket ${ticketNum} auto-triggered.`);
      } else {
        toast.success('Requisition submitted and queued for approval');
      }
      setOpenNewModal(false);
      setForm(EMPTY_FORM);
      await fetchRequests();
    } catch (e: any) {
      toast.error(e.message || 'Failed to submit requisition');
    } finally {
      setBusy(null);
    }
  };

  // Employees can only report on their own assigned assets (/my). Managers/admins see all.
  const reportableAssets = useMemo(() => {
    const source = isManager ? assets : myAssets;
    return (source || []).filter((a) => a.status !== 'Retired' && a.status !== 'Disposed');
  }, [myAssets, assets, isManager]);
  const selectedIssueAsset = useMemo(
    () => reportableAssets.find((a) => a.id === issueForm.assetId) || null,
    [reportableAssets, issueForm.assetId]
  );

  // Handle reporting an asset issue -> creates a support ticket linked to the asset
  const handleSubmitIssue = async () => {
    const asset = selectedIssueAsset;
    if (!asset) {
      toast.error('Please choose which asset has the issue');
      return;
    }
    if (!issueForm.description.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    setBusy('issue');
    try {
      const subject = `${issueForm.problemType}: ${asset.name || asset.asset_code} (${asset.asset_code})`;
      const created = await createTicket({
        subject,
        description: issueForm.description.trim(),
        type: 'Fault',
        priority: issueForm.priority,
        urgency: issueForm.priority,
        impact: 'medium',
        category: asset.category || asset.type || 'Hardware',
        assetCategory: asset.category || asset.type || 'Hardware',
        primaryAssetId: asset.id,
        assetId: asset.id,
        assetTag: asset.asset_code,
        assetCode: asset.asset_code,
        originModule: 'assets',
        source: 'Asset App',
        client: user?.name || asset.assignedToName || 'Asset User',
        requesterName: user?.name || asset.assignedToName || 'Asset User',
        requesterEmail: user?.email || '',
        requesterId: user?.id || undefined,
      });
      const ticketNo = (created as any)?.ticketNumber || (created ? `TB#${created.id}` : '');
      toast.success(ticketNo ? `Issue reported — ticket ${ticketNo} created` : 'Issue reported');
      setOpenNewModal(false);
      setIssueForm(EMPTY_ISSUE);
    } catch (e: any) {
      toast.error(e.message || 'Failed to report the issue');
    } finally {
      setBusy(null);
    }
  };

  // Open the modal in a given mode with fresh forms
  const openModal = (mode: 'request' | 'issue') => {
    setModalMode(mode);
    setForm(EMPTY_FORM);
    setIssueForm(EMPTY_ISSUE);
    setOpenNewModal(true);
  };

  // Handle reviewing request (Approve / Reject)
  const handleConfirmReview = async () => {
    if (!reviewDialog) return;
    const { request, status, notes } = reviewDialog;
    setBusy(request.id);
    try {
      await updateRequestStatus(request.id, status, {
        approvalNotes: notes.trim() || undefined,
      });

      // Best-effort ticket status sync
      try {
        const { useUnifiedSyncStore } = await import('../../../store/unifiedSyncStore');
        await useUnifiedSyncStore.getState().syncModuleActionToTicket({
          recordType: 'asset_requisition',
          recordId: request.id,
          action: status,
          moduleStatus: status,
          note: `Requisition ${status} by asset manager with note: ${notes || 'No notes'}`,
        });
      } catch (err) {
        console.warn('Sync to ticket fallback:', err);
      }

      toast.success(
        status === 'approved'
          ? `Approved request ${request.request_number || ''} for ${request.requester_name}`
          : `Rejected request ${request.request_number || ''}`
      );
      setReviewDialog(null);
      if (detailRequest?.id === request.id) {
        setDetailRequest(null);
      }
    } catch (e: any) {
      toast.error(e.message || 'Failed to update request');
    } finally {
      setBusy(null);
    }
  };

  // Table columns definition
  const columns: Column<AssetRequest>[] = [
    {
      id: 'request_number',
      header: 'Request #',
      width: '130px',
      cell: (r) => (
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={() => setDetailRequest(r)}
            className="font-mono text-xs font-semibold text-primary hover:underline text-left"
          >
            {r.request_number || r.requestNumber || `ARQ-${r.id.slice(0, 5)}`}
          </button>
          {(r.ticket_id || r.ticket_no) && (
            <a
              href={`/support/tickets?ticketId=${encodeURIComponent(r.ticket_id || r.ticket_no || '')}`}
              onClick={(e) => e.stopPropagation()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-[11px] text-info-strong hover:underline w-fit"
              title="Open auto-linked support ticket in Support Hub"
            >
              <LifeBuoy className="size-2.5" />
              {r.ticket_id || r.ticket_no}
            </a>
          )}
        </div>
      ),
      sortable: true,
      sortAccessor: (r) => r.request_number || r.requestNumber || r.id,
    },
    {
      id: 'requester',
      header: 'Requester',
      width: '220px',
      cell: (r) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <UserAvatar name={r.requester_name} email={r.requester_email} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{r.requester_name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {r.department || r.requester_department || r.requester_email || 'General'}
            </p>
          </div>
        </div>
      ),
      sortable: true,
      sortAccessor: (r) => r.requester_name,
    },
    {
      id: 'asset_type',
      header: 'Requested Asset',
      width: '200px',
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{r.asset_type}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {r.delivery_location && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="size-3 shrink-0" />
                {r.delivery_location}
              </span>
            )}
            {r.estimated_cost ? (
              <span className="flex items-center gap-0.5">
                <DollarSign className="size-3 shrink-0" />
                {r.estimated_cost.toLocaleString()}
              </span>
            ) : null}
          </div>
        </div>
      ),
      sortable: true,
      sortAccessor: (r) => r.asset_type,
    },
    {
      id: 'priority',
      header: 'Priority',
      width: '100px',
      cell: (r) => (
        <Badge variant={PRIORITY_TONE[r.priority]} className="capitalize font-normal text-xs">
          {r.priority}
        </Badge>
      ),
      sortable: true,
      sortAccessor: (r) => r.priority,
    },
    {
      id: 'status',
      header: 'Status',
      width: '180px',
      cell: (r) => <RequestStatus request={r} />,
      sortable: true,
      sortAccessor: (r) => r.status,
    },
    {
      id: 'review_info',
      header: 'Reviewer / Handover',
      width: '180px',
      cell: (r) => {
        if (r.status === 'pending') {
          return <span className="text-xs text-muted-foreground italic">Awaiting review</span>;
        }
        if (r.status === 'fulfilled') {
          return (
            <span className="text-xs text-success-strong font-medium">
              Device: {r.assigned_asset_code || 'Assigned'}
            </span>
          );
        }
        return (
          <div className="min-w-0 text-xs">
            <span className="text-foreground font-medium truncate block">
              {r.reviewer_name || 'Admin'}
            </span>
            {r.approval_notes && (
              <span className="text-muted-foreground truncate block">{r.approval_notes}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'date',
      header: 'Date',
      width: '110px',
      cell: (r) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
        </span>
      ),
      sortable: true,
      sortAccessor: (r) => r.createdAt,
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      width: '160px',
      cell: (r) => (
        <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
          {/* Quick Review Actions for Managers */}
          {isManager && r.status === 'pending' ? (
            <>
              <Button
                size="xs"
                variant="secondary"
                className="h-7 text-xs border-success/30 text-success-strong hover:bg-success/10 hover:border-success/60"
                onClick={() => setReviewDialog({ request: r, status: 'approved', notes: '' })}
                disabled={busy === r.id}
              >
                <CheckCircle2 className="size-3.5 mr-1" />
                Approve
              </Button>
              <Button
                size="xs"
                variant="secondary"
                className="h-7 text-xs border-destructive/30 text-destructive hover:bg-destructive/10 hover:border-destructive/60"
                onClick={() => setReviewDialog({ request: r, status: 'rejected', notes: '' })}
                disabled={busy === r.id}
              >
                <XCircle className="size-3.5 mr-1" />
                Reject
              </Button>
            </>
          ) : r.status === 'approved' ? (
            <Button
              size="xs"
              variant="secondary"
              className="h-7 text-xs text-primary hover:bg-primary/10"
              onClick={() => navigate('/assets/assign')}
            >
              Assign Device <ArrowRight className="size-3 ml-1" />
            </Button>
          ) : (
            <Button
              size="xs"
              variant="ghost"
              className="h-7 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setDetailRequest(r)}
            >
              <Eye className="size-3.5 mr-1" /> Details
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-4">
      {/* Page Header */}
      <PageHeader
        className="mb-0"
        icon={ClipboardList}
        title="Requests & Approvals"
        description="Raise employee hardware requisitions, review pending requests, and track device fulfillment lifecycle."
        actions={
          <div className="flex items-center gap-2">
            {counts.approved > 0 && (
              <Button
                variant="secondary"
                onClick={() => navigate('/assets/assign')}
                className="hidden sm:inline-flex"
              >
                Device Assignment ({counts.approved}) <ArrowRight className="size-4 ml-1" />
              </Button>
            )}
            <Button variant="secondary" onClick={() => openModal('issue')}>
              <Wrench className="size-4 mr-1.5" /> Report an Issue
            </Button>
            <Button onClick={() => openModal('request')}>
              <Plus className="size-4 mr-1.5" /> New Request
            </Button>
          </div>
        }
      />

      {/* KPI Summary Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          className={`p-4 cursor-pointer transition-colors border ${
            activeTab === 'all' ? 'border-primary/50 bg-secondary/20' : 'hover:border-border/80'
          }`}
          onClick={() => setActiveTab('all')}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">Total Requests</p>
            <ClipboardList className="size-4 text-muted-foreground" />
          </div>
          <p className="mt-2 font-display text-metric text-foreground">{counts.all}</p>
          <p className="mt-1 text-xs text-muted-foreground">All requisitions across organization</p>
        </Card>

        <Card
          className={`p-4 cursor-pointer transition-colors border ${
            activeTab === 'pending'
              ? 'border-warning/50 bg-warning/5'
              : 'hover:border-border/80'
          }`}
          onClick={() => setActiveTab('pending')}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-warning-strong">Pending Approval</p>
            <Clock className="size-4 text-warning-strong" />
          </div>
          <p className="mt-2 font-display text-metric text-foreground">{counts.pending}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {counts.pending === 0 ? 'Queue is clear' : 'Awaiting manager approval'}
          </p>
        </Card>

        <Card
          className={`p-4 cursor-pointer transition-colors border ${
            activeTab === 'approved'
              ? 'border-info/50 bg-info/5'
              : 'hover:border-border/80'
          }`}
          onClick={() => setActiveTab('approved')}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-info">Approved for Handover</p>
            <CheckSquare className="size-4 text-info" />
          </div>
          <p className="mt-2 font-display text-metric text-foreground">{counts.approved}</p>
          <p className="mt-1 text-xs text-muted-foreground">Ready for device provisioning</p>
        </Card>

        <Card
          className={`p-4 cursor-pointer transition-colors border ${
            activeTab === 'fulfilled'
              ? 'border-success/50 bg-success/5'
              : 'hover:border-border/80'
          }`}
          onClick={() => setActiveTab('fulfilled')}
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-success-strong">Fulfilled Devices</p>
            <CheckCircle2 className="size-4 text-success-strong" />
          </div>
          <p className="mt-2 font-display text-metric text-foreground">{counts.fulfilled}</p>
          <p className="mt-1 text-xs text-muted-foreground">Delivered & confirmed</p>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card className="overflow-hidden">
        {/* Navigation Tabs Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5 bg-card">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-1">
            {[
              { id: 'all', label: 'All Requests', count: counts.all },
              { id: 'pending', label: 'Pending Approval', count: counts.pending, alert: counts.pending > 0 },
              { id: 'approved', label: 'Approved', count: counts.approved },
              { id: 'fulfilled', label: 'Fulfilled', count: counts.fulfilled },
              { id: 'rejected', label: 'Rejected', count: counts.rejected },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as TabFilter)}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-secondary text-foreground font-semibold shadow-xs'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`rounded-full px-1.5 py-0.2 text-[10px] font-semibold ${
                    activeTab === tab.id
                      ? tab.alert
                        ? 'bg-warning/20 text-warning-strong'
                        : 'bg-muted text-foreground'
                      : tab.alert
                      ? 'bg-warning/15 text-warning-strong'
                      : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          <Button
            size="xs"
            variant="ghost"
            onClick={() => fetchRequests()}
            className="text-muted-foreground hover:text-foreground text-xs"
            title="Refresh requests"
          >
            <RotateCw className="size-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 bg-muted/20 px-4 py-2.5">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search by ARQ #, requester, asset type, department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 w-full rounded-md border border-input bg-card pl-9 pr-3 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Filter className="size-3" /> Priority:
            </span>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="h-8 rounded-md border border-input bg-card px-2.5 text-xs text-foreground outline-none focus:border-primary"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={paginatedData}
          keyExtractor={(r) => r.id}
          isLoading={isLoading && requests.length === 0}
          onRowClick={(r) => setDetailRequest(r)}
          empty={
            <EmptyState
              className="m-6 border-0"
              icon={<ClipboardList className="size-6" />}
              title={
                searchQuery || priorityFilter !== 'all'
                  ? 'No matching requests'
                  : activeTab === 'pending'
                  ? 'No pending approvals'
                  : 'No requisitions recorded yet'
              }
              description={
                searchQuery || priorityFilter !== 'all'
                  ? 'Try clearing or modifying your search filters.'
                  : 'Employees can submit hardware requisitions here, which route directly to managers for review.'
              }
              action={
                <Button size="sm" onClick={() => openModal('request')}>
                  <Plus className="size-4 mr-1.5" /> Submit Request
                </Button>
              }
            />
          }
          footer={
            filteredRequests.length > 0 ? (
              <div className="border-t border-border px-4 py-3">
                <Pagination
                  page={page}
                  pageCount={Math.ceil(filteredRequests.length / pageSize)}
                  onPageChange={setPage}
                  total={filteredRequests.length}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[10, 20, 50, 100]}
                  itemLabel="Requisitions"
                />
              </div>
            ) : null
          }
        />
      </Card>

      {/* Modal: New Requisition */}
      <Dialog open={openNewModal} onOpenChange={setOpenNewModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{modalMode === 'issue' ? 'Report an Asset Issue' : 'New Asset Request'}</DialogTitle>
            <DialogDescription>
              {modalMode === 'issue'
                ? 'Report a problem with an asset you have. A support ticket is created and routed to the team.'
                : "Tell us what you need. You'll get a tracking number and it goes to your manager for approval."}
            </DialogDescription>
          </DialogHeader>

          {/* Mode toggle: New request / Report an issue */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-secondary/40 p-0.5 text-sm">
            <button
              type="button"
              onClick={() => setModalMode('request')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                modalMode === 'request' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Plus className="size-3.5" /> New request
            </button>
            <button
              type="button"
              onClick={() => setModalMode('issue')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                modalMode === 'issue' ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Wrench className="size-3.5" /> Report an issue
            </button>
          </div>

          <div className="space-y-3.5 py-1 text-sm max-h-[70vh] overflow-y-auto pr-1">
            {modalMode === 'request' && (<>
            {/* What do you need */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                What do you need? <span className="text-destructive">*</span>
              </label>
              <input
                className={fieldClass}
                list="asset-request-types"
                placeholder="e.g. Laptop, 27-inch Monitor, Wireless Mouse"
                value={form.asset_type}
                maxLength={100}
                onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
              />
              <datalist id="asset-request-types">
                {categories.map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
                <option value="Laptop" />
                <option value="Desktop" />
                <option value="Monitor" />
                <option value="Mobile Phone" />
                <option value="Tablet" />
                <option value="Keyboard" />
                <option value="Mouse" />
                <option value="Headset" />
                <option value="Docking Station" />
                <option value="Printer" />
              </datalist>
              {/* Quick picks — fill the field in one tap */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {PRESET_CATEGORIES.filter((cat) => cat.id !== 'other').map((cat) => {
                  const Icon = cat.icon;
                  const active = form.asset_type.trim().toLowerCase() === cat.label.toLowerCase();
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setForm({ ...form, asset_type: cat.label })}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary font-semibold'
                          : 'bg-card text-muted-foreground border-border hover:bg-secondary/60 hover:text-foreground'
                      }`}
                    >
                      <Icon className="size-3.5" /> {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Quantity</label>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className={fieldClass}
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Priority</label>
                <select
                  className={fieldClass}
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value as AssetRequest['priority'] })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Department <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  className={fieldClass}
                  placeholder="e.g. Engineering, Sales"
                  value={form.department}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Delivery location <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <input
                  className={fieldClass}
                  placeholder="e.g. HQ Floor 3 / Remote"
                  value={form.delivery_location}
                  maxLength={100}
                  onChange={(e) => setForm({ ...form, delivery_location: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                Reason / details <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <textarea
                className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder="Why do you need this? Add any specs (e.g. 16GB RAM, 512GB SSD) or notes."
                value={form.reason}
                maxLength={2000}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
              />
              <p className="mt-1 text-[11px] text-muted-foreground text-right">{form.reason.length}/2000</p>
            </div>
            </>)}

            {modalMode === 'issue' && (
              reportableAssets.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  You don't have any assets to report an issue on yet.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Which asset has the issue? <span className="text-destructive">*</span>
                    </label>
                    <select
                      className={fieldClass}
                      value={issueForm.assetId}
                      onChange={(e) => setIssueForm({ ...issueForm, assetId: e.target.value })}
                    >
                      <option value="">Select an asset…</option>
                      {reportableAssets.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.asset_code} — {a.name || a.model || a.category || 'Asset'}
                          {a.assignedToName ? ` · ${a.assignedToName}` : ''}
                        </option>
                      ))}
                    </select>
                    {selectedIssueAsset && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {[selectedIssueAsset.type, selectedIssueAsset.category, selectedIssueAsset.serial_number && `SN ${selectedIssueAsset.serial_number}`].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Problem type</label>
                      <select
                        className={fieldClass}
                        value={issueForm.problemType}
                        onChange={(e) => setIssueForm({ ...issueForm, problemType: e.target.value })}
                      >
                        {ISSUE_PROBLEM_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Priority</label>
                      <select
                        className={fieldClass}
                        value={issueForm.priority}
                        onChange={(e) => setIssueForm({ ...issueForm, priority: e.target.value as AssetRequest['priority'] })}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground mb-1">
                      Describe the issue <span className="text-destructive">*</span>
                    </label>
                    <textarea
                      className="min-h-24 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                      placeholder="What's wrong? When did it start? Any error messages?"
                      value={issueForm.description}
                      maxLength={2000}
                      onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground text-right">{issueForm.description.length}/2000</p>
                  </div>
                </>
              )
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpenNewModal(false)} disabled={busy === 'new' || busy === 'issue'}>
              Cancel
            </Button>
            {modalMode === 'issue' ? (
              <Button onClick={handleSubmitIssue} isLoading={busy === 'issue'} disabled={reportableAssets.length === 0}>
                Report Issue
              </Button>
            ) : (
              <Button onClick={handleSubmitNew} isLoading={busy === 'new'}>
                Submit Request
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Review Requisition (Approve or Reject with Notes) */}
      <Dialog open={!!reviewDialog} onOpenChange={(open) => !open && setReviewDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reviewDialog?.status === 'approved' ? (
                <>
                  <CheckCircle2 className="size-5 text-success-strong" /> Approve Requisition
                </>
              ) : (
                <>
                  <XCircle className="size-5 text-destructive" /> Reject Requisition
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog?.status === 'approved'
                ? `Approving ${reviewDialog.request.request_number || 'request'} moves it to 'Approved', allowing asset fulfillment and device assignment.`
                : `Rejecting ${reviewDialog?.request.request_number || 'request'} terminates this requisition.`}
            </DialogDescription>
          </DialogHeader>

          {reviewDialog && (
            <div className="space-y-3 py-2 text-sm">
              <div className="rounded-lg border border-border bg-secondary/30 p-3 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Requester:</span>
                  <span className="font-medium text-foreground">{reviewDialog.request.requester_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Asset:</span>
                  <span className="font-medium text-foreground">{reviewDialog.request.asset_type}</span>
                </div>
                {reviewDialog.request.reason && (
                  <div className="pt-1 border-t border-border/50">
                    <span className="text-muted-foreground block mb-0.5">Justification:</span>
                    <span className="text-foreground italic">{reviewDialog.request.reason}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  {reviewDialog.status === 'approved' ? 'Approval Notes (Optional)' : 'Rejection Reason (Required)'}
                </label>
                <textarea
                  className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={
                    reviewDialog.status === 'approved'
                      ? 'Add any provisioning instructions, allocated budget, or vendor notes...'
                      : 'State clearly why this requisition cannot be approved at this time...'
                  }
                  value={reviewDialog.notes}
                  maxLength={1000}
                  onChange={(e) => setReviewDialog({ ...reviewDialog, notes: e.target.value })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setReviewDialog(null)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button
              variant={reviewDialog?.status === 'approved' ? 'default' : 'destructive'}
              onClick={handleConfirmReview}
              isLoading={busy !== null}
            >
              {reviewDialog?.status === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Full Request Inspection / Details */}
      <Dialog open={!!detailRequest} onOpenChange={(open) => !open && setDetailRequest(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-base font-bold text-foreground">
                  {activeDetailRequest?.request_number || activeDetailRequest?.requestNumber || 'Requisition'}
                </span>
                {activeDetailRequest && <RequestStatus request={activeDetailRequest} />}
                {(activeDetailRequest?.ticket_id || activeDetailRequest?.ticket_no) && (
                  <a
                    href={`/support/tickets?ticketId=${encodeURIComponent(activeDetailRequest.ticket_id || activeDetailRequest.ticket_no || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-mono text-xs font-semibold bg-info/10 text-info-strong border border-info/20 hover:bg-info/20 hover:border-info/30 transition-colors"
                    title="Open auto-linked support ticket in Support Hub"
                  >
                    <LifeBuoy className="size-3 text-info" />
                    <span>{activeDetailRequest.ticket_id || activeDetailRequest.ticket_no}</span>
                  </a>
                )}
              </div>
              {activeDetailRequest && (
                <Badge variant={PRIORITY_TONE[activeDetailRequest.priority]} className="capitalize">
                  {activeDetailRequest.priority} Priority
                </Badge>
              )}
            </div>
            <DialogDescription>
              Detailed view of hardware requisition context, approval decision, and lifecycle history.
            </DialogDescription>
          </DialogHeader>

          {activeDetailRequest && (
            <div className="space-y-4 py-2 text-xs sm:text-sm">
              {/* Requester Profile */}
              <div className="rounded-lg border border-border bg-secondary/20 p-3.5">
                <div className="flex items-center gap-3">
                  <UserAvatar
                    name={activeDetailRequest.requester_name}
                    email={activeDetailRequest.requester_email}
                    size="md"
                  />
                  <div>
                    <p className="font-semibold text-foreground text-sm">{activeDetailRequest.requester_name}</p>
                    <p className="text-xs text-muted-foreground">{activeDetailRequest.requester_email || 'No email'}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Building className="size-3" />
                        {activeDetailRequest.department || activeDetailRequest.requester_department || 'General'}
                      </span>
                      {activeDetailRequest.delivery_location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="size-3" />
                          {activeDetailRequest.delivery_location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Hardware Spec */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-3">
                  <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <Laptop className="size-3.5" /> Requested Hardware
                  </span>
                  <p className="font-medium text-foreground">{activeDetailRequest.asset_type}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                    <DollarSign className="size-3.5" /> Estimated Cost
                  </span>
                  <p className="font-medium text-foreground">
                    {activeDetailRequest.estimated_cost ? `$${activeDetailRequest.estimated_cost.toLocaleString()}` : 'Not specified'}
                  </p>
                </div>
              </div>

              {/* Dynamic Specifications & Details Grid */}
              {activeDetailRequest.specifications && Object.keys(activeDetailRequest.specifications).length > 0 && (
                <div className="rounded-lg border border-border bg-card p-3 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <SlidersHorizontal className="size-3.5 text-primary" />
                    <span>Dynamic Hardware Specifications</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {Object.entries(activeDetailRequest.specifications)
                      .filter(([_, v]) => v !== undefined && v !== null && String(v).trim() !== '')
                      .map(([key, val]) => (
                        <div key={key} className="rounded-md border border-border/60 bg-secondary/30 p-2 text-xs">
                          <span className="text-[11px] text-muted-foreground block truncate capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className="font-medium text-foreground truncate block mt-0.5" title={String(val)}>
                            {String(val)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Auto-Linked Support Ticket Card */}
              {(activeDetailRequest.ticket_id || activeDetailRequest.ticket_no) ? (
                <div className="rounded-lg border border-info/25 bg-info/5 p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-8 items-center justify-center rounded-md bg-info/15 text-info-strong shrink-0">
                      <LifeBuoy className="size-4" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Support Ticket:</span>
                        <span className="font-mono text-xs font-bold text-foreground">
                          {activeDetailRequest.ticket_id || activeDetailRequest.ticket_no}
                        </span>
                        <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-semibold bg-info/15 text-info-strong">
                          Auto-Triggered
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Linked Service Request in Support Desk for device procurement & fulfillment
                      </p>
                    </div>
                  </div>
                  <a
                    href={`/support/tickets?ticketId=${encodeURIComponent(activeDetailRequest.ticket_id || activeDetailRequest.ticket_no || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline px-2.5 py-1.5 rounded-md hover:bg-primary/5 transition-colors shrink-0 ml-2"
                  >
                    View Ticket <ExternalLink className="size-3" />
                  </a>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-secondary/10 p-2.5 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <LifeBuoy className="size-3.5 text-muted-foreground" />
                    Support Ticket
                  </span>
                  <span className="italic">Auto-generating ticket in Support Desk...</span>
                </div>
              )}

              {/* Business Justification */}
              <div className="rounded-lg border border-border p-3">
                <span className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                  <FileText className="size-3.5" /> Business Justification & Notes
                </span>
                <p className="text-foreground whitespace-pre-wrap">
                  {activeDetailRequest.reason || activeDetailRequest.notes || 'No reason provided.'}
                </p>
              </div>

              {/* Approval Lifecycle Info */}
              {(activeDetailRequest.reviewer_name || activeDetailRequest.approval_notes || activeDetailRequest.status !== 'pending') && (
                <div className="rounded-lg border border-border p-3 space-y-1.5 bg-secondary/10">
                  <span className="text-xs font-semibold text-foreground flex items-center gap-1">
                    <CheckSquare className="size-3.5 text-primary" /> Approval Decision
                  </span>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Reviewer:</span>{' '}
                      <span className="font-medium text-foreground">{activeDetailRequest.reviewer_name || 'System / Admin'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Decision:</span>{' '}
                      <span className="font-medium capitalize text-foreground">{activeDetailRequest.status}</span>
                    </div>
                  </div>
                  {activeDetailRequest.approval_notes && (
                    <div className="pt-1.5 border-t border-border/50 text-xs">
                      <span className="text-muted-foreground block">Review Notes:</span>
                      <p className="text-foreground italic mt-0.5">{activeDetailRequest.approval_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Fulfillment Info */}
              {activeDetailRequest.status === 'fulfilled' && (
                <div className="rounded-lg border border-success/30 bg-success/5 p-3 text-xs">
                  <span className="font-semibold text-success-strong flex items-center gap-1 mb-1">
                    <CheckCircle2 className="size-3.5" /> Handover Complete
                  </span>
                  <p className="text-foreground">
                    Assigned Asset Code:{' '}
                    <span className="font-mono font-medium">{activeDetailRequest.assigned_asset_code || 'N/A'}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex items-center justify-between">
            <div>
              {activeDetailRequest && isManager && activeDetailRequest.status === 'pending' && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="bg-primary text-primary-foreground"
                    onClick={() => {
                      setReviewDialog({ request: activeDetailRequest, status: 'approved', notes: '' });
                    }}
                  >
                    <CheckCircle2 className="size-3.5 mr-1" /> Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="border-destructive/30 text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      setReviewDialog({ request: activeDetailRequest, status: 'rejected', notes: '' });
                    }}
                  >
                    <XCircle className="size-3.5 mr-1" /> Reject
                  </Button>
                </div>
              )}
              {activeDetailRequest && activeDetailRequest.status === 'approved' && (
                <Button
                  size="sm"
                  className="bg-primary text-primary-foreground"
                  onClick={() => navigate('/assets/assign')}
                >
                  Go to Device Assignment <ArrowRight className="size-3.5 ml-1" />
                </Button>
              )}
            </div>
            <Button variant="secondary" onClick={() => setDetailRequest(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
